import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import OnboardingOTP from "./components/OnboardingOTP";
import "./styles/MultiStepOnboarding.css";
import ClientSummary from "./components/ClientSummary";
import ClientDocumentUpload from "./components/ClientDocumentUpload";
import RiskProfile from "./components/RiskProfile";


// Configure axios AFTER imports
axios.defaults.baseURL =  "/wfmb";
axios.defaults.withCredentials = true;


/* ================== VALIDATIONS ================== */
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const mobileRegex = /^\d{7,15}$/; // works for all countries
const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const accRegex = /^\d{9,18}$/;
const micrRegex = /^\d{9}$/;
const nomineePanRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;        // PAN format
const nomineeAadhaarRegex = /^\d{12}$/;                   // Aadhaar 12 digits only
const nomineeDrivingRegex = /^[A-Z0-9]{1,13}$/;           // Driving Licence 1–13 alphanumeric
const formatDateForInput = (date) => {
  if (!date) return "";
  return date.split("T")[0]; // YYYY-MM-DD
};
const monthsMap = {
  Jan: "01", Feb: "02", Mar: "03", Apr: "04",
  May: "05", Jun: "06", Jul: "07", Aug: "08",
  Sep: "09", Oct: "10", Nov: "11", Dec: "12"
};

const formatDateToText = (yyyyMMdd) => {
  if (!yyyyMMdd) return "";
  const [y, m, d] = yyyyMMdd.split("-");
  const month = Object.keys(monthsMap).find(k => monthsMap[k] === m);
  return month ? `${d}/${month}/${y}` : "";
};


const parseTextToDate = (text) => {
  text = text.trim();
  
  // Case 1: DD/Mon/YYYY (e.g., 05/Jan/1990)
  let match1 = text.match(/^(\d{2})\/([A-Za-z]{3})\/(\d{4})$/);
  if (match1) {
    const [, d, mon, y] = match1;
    const m = monthsMap[mon];
    if (!m) return "";
    return `${y}-${m}-${d}`; // YYYY-MM-DD for backend
  }

  // Case 2: DDMMYYYY (e.g., 09012026)
  let match2 = text.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (match2) {
    const [, d, m, y] = match2;
    const month = Object.keys(monthsMap).find(k => monthsMap[k] === m);
    if (!month) return "";
    return `${y}-${m}-${d}`;
  }

  return ""; // invalid format
};
const STEP_CONFIRM = 8;
const STEP_PDF = 9;
const STEP_DOCUMENT_UPLOAD = 10;
const STEP_RISK_PROFILE = 11;

/* ================== STEP FIELD MAP ================== */
const stepFieldMap = {
  1: ["pan"],
  2: ["full_name", "email", "mobile"],
  3: [],
  4: ["date_of_birth", "gender", "occupation", "marital_status"],
  5: ["address_line", "street", "area", "city", "state", "pincode"],
  6: ["bank_name", "branch_name", "account_type", "account_number", "ifsc", "micr"],
  7: [
    "nominee_name","nominee_relation","nominee_id_type","nominee_id_number",
    "nominee_email","nominee_contact","nominee_is_minor",
    "nominee_address","nominee_street","nominee_area",
    "nominee_city","nominee_state","nominee_pincode"
  ]
};

/* ================== AUTO-SAVE CHECK ================== */
const canAutoSaveStep = (stepNum, formData) => {
  const fields = stepFieldMap[stepNum] || [];
  const optional = stepNum === 6 ? ["micr"] : stepNum === 7 ? ["nominee_dob", "guardian_name"] : [];
  return fields.every(f => optional.includes(f) || (formData[f] && formData[f].toString().trim() !== ""));
};

/* ================== CONSTANTS ================== */
const indianStates = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu & Kashmir","Ladakh"
];
const occupations = ["Agriculturist","Banking / Financial Institutions","Business","Business Manufacturing","Business Trading","Doctor","Forex Dealer",
  "Government Service","Housewife","Indian Private Company Employee","Lawyer","Multinational Employee","NA","Non-Government Service",
  "Other Business","Other Service","Private Sector Service","Profession - Finance","PSU/Government Employee",
  "Public Sector / Government Service","Retired","Salaried","Scientist","Self Employed","Student","Teacher"];
const accountTypes = ["Savings","Current"];
const nomineeRelations = ["AUNT","BROTHER","BROTHER-IN-LAW","COURT APPOINTED LEGAL GUARDIAN","DAUGHTER","DAUGHTER-IN-LAW","FATHER","FATHER-IN-LAW",
  "GRAND DAUGHTER","GRAND FATHER","GRAND MOTHER","GRAND SON","MOTHER","MOTHER-IN-LAW","NEPHEW","NIECE","OTHERS",
  "SISTER","SISTER-IN-LAW","SON","SON-IN-LAW","SPOUSE","UNCLE"];
// FIXED: only "PAN" is actually supported end-to-end (DB column is Nom1PAN, Char(10), and the
// BSE SOAP method only carries a nominee PAN — no separate field/code for Aadhaar or Driving
// Licence). Previously offering AADHAAR/DRIVING LICENCE here let users pick them, but the value
// got silently truncated into Nom1PAN and BSE was still told identity type = 1 (PAN) regardless
// — i.e. wrong data submitted to BSE. Do NOT re-add these until the DB schema + BSE mapping in
// server.js/bseRoutes.js are extended to actually support identity types 2/3/4 correctly.
const nomineeIdTypes = ["PAN"];
const maritalStatuses = ["Married","Single","Separated","Other"];
const countryCodes = [
  "+91",  // India
  "+1",   // USA
  "+44",  // UK
  "+61",  // Australia
  "+971", // UAE
  "+81",  // Japan
  "+49"   // Germany
];

/* ================== DEBOUNCE ================== */
const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};
const OnlineTransactionConfirm = ({ onYes, onNo }) => {
  return (
    <div className="confirmation-box">
      <h2 className="confirm-title">Welcome to WEALTH FIRST</h2>
      <p className="confirm-sub">You are at the final step of registration</p>

      <h3 className="confirm-question">
        Do you want to register for Online Transaction?
      </h3>

      <button onClick={onYes}>Yes, Continue</button>
      <button className="outline-btn" onClick={onNo}>
        No, Cancel
      </button>

      <p className="confirm-help">
        In case you are stuck at any stage, please contact support.
      </p>
    </div>
  );
};
const initialFormState = {
  id: "", pan: "", full_name: "", email: "", country_code: "+91", mobile: "",
  date_of_birth: "", gender: "", occupation: "", marital_status: "",
  address_line: "", street: "", area: "", city: "", state: "", pincode: "",
  account_type: "", bank_name: "", branch_name: "",
  account_number: "", ifsc: "", micr: "",
  nominee_name: "", nominee_relation: "", nominee_id_type: "",
  nominee_id_number: "", nominee_email: "", nominee_contact: "",
  nominee_is_minor: "No", nominee_dob: "", guardian_name: "",
  nominee_address: "", nominee_street: "", nominee_area: "",
  nominee_city: "", nominee_state: "", nominee_pincode: "", same_address: false,
};
export default function MultiStepOnboarding() {
  const [panName, setPanName] = useState(`pan_${Date.now()}`);
  const otpRequestRef = useRef(false); 
  const [step, setStep] = useState(1);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const isFetchingRef = useRef(false);
    const [panExists, setPanExists] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [sendingOtp, setSendingOtp] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [saving, setSaving] = useState(false);  // NEW: show auto-save status
    const [form, setForm] = useState(initialFormState);
    // Fields that admin cannot edit because already completed
const [readonlyFields, setReadonlyFields] = useState([]);

    const location = useLocation();
const params = new URLSearchParams(location.search);

const adminMode = params.get("admin") === "true";
const adminPan = params.get("pan");
const adminStep = Number(params.get("step"));
    
  // 🔹 DOB text states (for manual typing)
const [dobText, setDobText] = useState("");
const [nomineeDobText, setNomineeDobText] = useState("");
const [showClientSummary, setShowClientSummary] = useState(false);
  // Reset form completely on page load (ignore old localStorage)

// Place this inside your component, after `initialFormState` is defined
const panRef = useRef(null);
 useEffect(() => {
  if (adminMode && adminPan) {

    setOtpVerified(true);
    setPanExists(true);

    loadAdminClient(adminPan);

    setTimeout(() => {
      setStep(adminStep || 2);

      // 🔥 IMPORTANT: trigger autosave for admin
      

    }, 500);

    axios.get(`/api/admin-done-fields?pan=${adminPan}`)
      .then(res => {
        setReadonlyFields(res.data.doneFields || []);
      })
      .catch(err => console.error(err));
  }
}, [adminMode, adminPan, adminStep]);

useEffect(() => {
  if (adminMode && adminPan) {
    loadAdminClient(adminPan);
  }
}, [step]);
  /* =================================================
   🔐 AUTO SKIP STEP-3 ONLY IF OTP VERIFIED BEFORE
   ================================================= */

 /* ================== CHECK PAN ================== */
const checkPan = async (pan) => {

  // 🔴 PAN REQUIRED
  if (!pan || !pan.trim()) {
    setError("PAN is required");
    return;
  }

  // 🔴 PAN FORMAT VALIDATION (10 chars)
  if (!panRegex.test(pan)) {
    setError("Invalid PAN format (ABCDE1234F)");
    return;
  }

  // 🔒 Prevent multiple API calls
  if (isFetchingRef.current) return;
  isFetchingRef.current = true;

 try {
  // 🔹 CALL BACKEND PAN CHECK API
  const res = await axios.post("/api/check-pan", { pan });

// NEW CODE
if (res.data.alreadyClient) {
  alert("You are already registered with WealthFirst.");
  return;
}

  if (res.data.clientExists) {
  const savedData = res.data.clientData || {};

  // Prefill form but keep entered PAN
  setForm(prev => ({
    ...prev,
    ...savedData,
    pan: prev.pan,
    account_type: savedData.account_type || "",
  nominee_id_type: savedData.nominee_id_type || "",
  
    date_of_birth: savedData.date_of_birth
      ? savedData.date_of_birth.split("T")[0]
      : "",
    nominee_dob: savedData.nominee_dob
      ? savedData.nominee_dob.split("T")[0]
      : "",
  }));

  setDobText(
    savedData.date_of_birth
      ? formatDateToText(savedData.date_of_birth.split("T")[0])
      : ""
  );
  setNomineeDobText(
    savedData.nominee_dob
      ? formatDateToText(savedData.nominee_dob.split("T")[0])
      : ""
  );

  setPanExists(true);
  setOtpVerified(savedData.currentStep > 2);

  // 🔥 MAIN CHANGE: STEP LOGIC
  if (!savedData.currentStep || savedData.currentStep < 3) {
    // OTP not yet verified → go to OTP page
    setStep(3);
  } else {
    // OTP already verified → skip to next step
    setStep(4);
  }

  setSuccess("Existing PAN detected. Form restored and OTP step opened.");
  setError("");
} else {
  // NEW PAN
  setPanExists(false);
  setOtpVerified(false);
  setStep(2); // Step 2 for entering name/email/mobile
  await autoSave({ pan: pan }, 1);
  setSuccess("New PAN detected. Continue registration.");
  setError("");
}


  } catch (err) {
  console.error("PAN check failed:", err.response?.data || err.message);
  setError(
    err.response?.data?.message || "PAN check failed. Try again."
  );
  setPanExists(false);

  } finally {
    isFetchingRef.current = false;
  }
};

const loadAdminClient = async (pan) => {
  try {
    const res = await axios.get(`/api/get-client-by-pan?pan=${pan}`);
    const data = res.data || {};

    // Fix DOB format for backend
    let dob = data.date_of_birth;
    if (dob) {
      const d = new Date(dob);
      dob = d.toISOString().split("T")[0]; // YYYY-MM-DD
    }

    const updated = {
      ...initialFormState,
      ...data,
      pan: data.pan || pan,
      date_of_birth: dob
    };

    setForm(updated);

    // ✅ THIS LINE IS THE FIX
    setDobText(updated.date_of_birth ? formatDateToText(updated.date_of_birth) : "");

    setReadonlyFields(prev => prev.filter(f => f !== "date_of_birth"));

    // Trigger autosave for admin
    const saveStep = 4; // DOB step
    setTimeout(() => {
      console.log("Admin autosave triggered for DOB:", updated.date_of_birth);
      debouncedAutoSave(updated, saveStep);
    }, 300);

  } catch (err) {
    console.error("Admin load error:", err);
  }
};

const autoSave = async (currentForm = form, currentStepNum = step) => {

  if (!currentForm.pan) return;   // only check if PAN exists

  // 🔹 SAVE FORM TO LOCAL STORAGE ON EVERY CHANGE
  // ✅ IIS-SAFE LOCAL AUTOSAVE (MUST)
localStorage.setItem("onboarding_form", JSON.stringify(currentForm));
localStorage.setItem("onboarding_step", currentStepNum);
  try {
    setSaving(true);

    const dataToSave = { ...currentForm, currentStep: Number(currentStepNum), id: currentForm.id || null };

    readonlyFields.forEach(f => delete dataToSave[f]);

    // Convert booleans to "Yes"/"No"
    dataToSave.same_address = dataToSave.same_address ? "Yes" : "No";
    dataToSave.nominee_is_minor = dataToSave.nominee_is_minor === "Yes" ? "Yes" : "No";

    // Optional: empty strings → null for MSSQL
    Object.keys(dataToSave).forEach(key => {
      if (dataToSave[key] === "") dataToSave[key] = null;
    });

    // Copy client address if same_address is true
    if (currentStepNum === 7 && dataToSave.same_address === "Yes") {
      dataToSave.nominee_address = dataToSave.address_line;
      dataToSave.nominee_street = dataToSave.street;
      dataToSave.nominee_area = dataToSave.area;
      dataToSave.nominee_city = dataToSave.city;
      dataToSave.nominee_state = dataToSave.state;
      dataToSave.nominee_pincode = dataToSave.pincode;
    }

   await axios.post("/api/auto-save", dataToSave);


  } finally {
    setSaving(false);
  }
};
const debouncedAutoSave = useRef(debounce(autoSave, 1000)).current;
/* ================== INPUT HANDLER ================== */
const update = (e) => {
  let value = e.target.value ?? "";
  const name = e.target.name;

  // Country code: default +91 and ensure "+"
  if (name === "country_code") {
    value = value || "+91";
    if (!value.startsWith("+")) value = "+" + value;
  }

  // PAN: uppercase, only A-Z0-9, max 10
  if (name === "pan") {
    value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
  }

  // IFSC: uppercase, max 11
  if (name === "ifsc") {
    value = value.toUpperCase().slice(0, 11);
  }
  // Numeric fields
  if (["mobile", "nominee_contact", "account_number", "micr", "pincode", "nominee_pincode"].includes(name)) {
    value = value.replace(/\D/g, ""); // keep digits only
     // Specific rules
    if (name === "mobile" || name === "nominee_contact") {
        // Limit to 10 digits only
        if (value.length > 10) value = value.slice(0, 10);
    }
    if (name === "account_number") value = value.slice(0, 18);
    if (name === "micr") value = value.slice(0, 9);
    if (name === "pincode" || name === "nominee_pincode") value = value.slice(0, 6);
  }

  // Nominee ID auto-format
  if (name === "nominee_id_number") {
    const currentType = (e.currentTarget.dataset.type || form.nominee_id_type).toUpperCase();

    if (currentType === "PAN") {
        value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
    } else if (currentType === "AADHAAR") {
        value = value.replace(/\D/g, "").slice(0, 12);
    } else if (currentType === "DRIVING LICENCE" || currentType === "DRIVING") {
        value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 13);
    }
}
  // Reset nominee ID when type changes
  if (name === "nominee_id_type") {
    setForm(prev => ({ ...prev, nominee_id_number: "", [name]: value }));
    setFieldErrors(prev => ({ ...prev, nominee_id_number: "" }));
    return;
  }

  // Update form and auto-save
setForm(prev => {
  const updated = { ...prev, [name]: value };

  // 🔥 autosave while typing
  debouncedAutoSave(updated, step || adminStep);

  return updated;
});

};

  /* ================== VALIDATION ================== */
const validateStep = () => {
  const errors = {};

  const requireField = (field, label) => {
    if (!form[field]?.toString().trim()) {
      errors[field] = `${label} is required`;
      return false;
    }
    return true;
  };

  const fields = stepFieldMap[step] || [];

  fields.forEach(field => {
    if (step === 6 && field === "micr") return; // optional
    if (
      step === 7 &&
      form.same_address &&
      field.startsWith("nominee_") &&
      !["nominee_name","nominee_relation","nominee_id_type","nominee_id_number","nominee_is_minor","nominee_dob","guardian_name"].includes(field)
    ) return;

    if (field === "pan") {
      if (requireField("pan", "PAN") && !panRegex.test(form.pan)) {
        errors.pan = "Invalid PAN format (ABCDE1234F)";
      }
    } else if (field === "email") {
      if (requireField("email", "Email") && !emailRegex.test(form.email)) {
        errors.email = "Invalid email format";
      }
    } else if (field === "mobile") {
    if (requireField("mobile", "Mobile")) {
        if (form.mobile.length !== 10) {
            errors.mobile = "Mobile number must be 10 digits";
        }
    }
    } else if (field === "account_number") {
      if (requireField("account_number", "Account Number") && !accRegex.test(form.account_number)) {
        errors.account_number = "Invalid account number";
      }
    } else if (field === "ifsc") {
      if (requireField("ifsc", "IFSC") && !ifscRegex.test(form.ifsc)) {
        errors.ifsc = "Invalid IFSC";
      }
    } else if (field === "nominee_id_number") {
      if (requireField("nominee_id_number", "Nominee ID Number")) {
        const idType = (form.nominee_id_type || "").toUpperCase();
        if (idType === "PAN" && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.nominee_id_number)) {
          errors.nominee_id_number = "Invalid PAN format (ABCDE1234F)";
        } else if (idType === "AADHAAR" && !/^\d{12}$/.test(form.nominee_id_number)) {
          errors.nominee_id_number = "Aadhaar must be 12 digits";
        } else if (idType === "DRIVING LICENCE" || idType === "DRIVING") {
          if (!/^[A-Z0-9]{1,13}$/.test(form.nominee_id_number)) {
            errors.nominee_id_number = "Invalid Driving Licence format";
          }
        }
      }
    } else if (field === "country_code") return;
    else {
      requireField(field, field.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()));
    }
  });

  // Step 7 minor / guardian checks
  if (step === 7) {
    requireField("nominee_name", "Nominee Name");
    requireField("nominee_relation", "Nominee Relation");
    requireField("nominee_id_type", "Nominee ID Type");
    requireField("nominee_id_number", "Nominee ID Number");
    requireField("nominee_email", "Nominee Email");
    if (!form.nominee_contact || form.nominee_contact.length !== 10) {
    errors.nominee_contact = "Nominee contact must be 10 digits";
}
    requireField("nominee_is_minor", "Minor/Adult Selection");

    if (form.nominee_is_minor === "Yes") {
      requireField("nominee_dob", "Nominee Date of Birth");
      requireField("guardian_name", "Guardian Name");
    }

    if (!form.same_address) {
      requireField("nominee_address", "Nominee Address");
      requireField("nominee_street", "Nominee Street");
      requireField("nominee_area", "Nominee Area");
      requireField("nominee_city", "Nominee City");
      requireField("nominee_state", "Nominee State");
      requireField("nominee_pincode", "Nominee Pincode");
    }
  }

  setFieldErrors(errors);
  setError(Object.values(errors)[0] || "");
  return Object.keys(errors).length === 0;
};
  /* ================== SUBMIT ================== */
  const handleSubmit = async () => {
  if (!form.pan) { alert("PAN is required!"); return; }
  if (!validateStep()) return;

  try {
    // ✅ Convert booleans to string for MSSQL
    const payload = {
  ...form,
  currentStep: Number(step),
  same_address: form.same_address ? "Yes" : "No",
  nominee_is_minor: form.nominee_is_minor === "Yes" ? "Yes" : "No",
  otp_verified: otpVerified ? "Yes" : "No",
  id: form.id || null
};

// 🔹 Ensure nominee address copied if same_address
if (form.same_address) {
  payload.nominee_address = form.address_line;
  payload.nominee_street = form.street;
  payload.nominee_area = form.area;
  payload.nominee_city = form.city;
  payload.nominee_state = form.state;
  payload.nominee_pincode = form.pincode;
};
    await axios.post("/api/submit-client", payload);


    setStep(STEP_CONFIRM);
setSuccess("");
    setError("");
  } catch (err) {
    console.error("FINAL SUBMIT ERROR:", err);
    setStep(8);
    setSuccess("✅ Form submitted (check logs)");
    setError("");
  }
};

 const nextStep = async () => {

  if (!validateStep(step)) return;

  const newStep = step + 1;

  setStep(newStep);

  try {
    await autoSave(form, newStep);
  } catch (err) {
    console.error("Next autosave error:", err);
  }
};

// ===== YES BUTTON API CALL =====

const handleYes = async () => {
  try {

    // Send COMPLETE form data
    const payload = {
      ...form,          // 🔥 all fields
      pan: form.pan     // ensure PAN included
    };

    await axios.post("/api/yes-continue", payload);

    setStep(STEP_PDF);

  } catch (err) {
    console.error("YES ERROR:", err);
    alert("YES stored procedure failed");
  }
};

// ===== NO BUTTON API CALL =====
const handleNo = async () => {
  try {
    // Prepare full payload
    const payload = {
      ...form,                       // all client data
      currentStep: Number(step),
      same_address: form.same_address ? "Yes" : "No",
      nominee_is_minor: form.nominee_is_minor === "Yes" ? "Yes" : "No",
      otp_verified: otpVerified ? "Yes" : "No",
      id: form.id || null
    };

    // Copy client address to nominee if same_address checked
    if (form.same_address) {
      payload.nominee_address = form.address_line;
      payload.nominee_street = form.street;
      payload.nominee_area = form.area;
      payload.nominee_city = form.city;
      payload.nominee_state = form.state;
      payload.nominee_pincode = form.pincode;
    }

    await axios.post("/api/no-cancel", payload);

    setShowClientSummary(true); // continue your flow
    setSuccess("Client data saved successfully (No/Cancel).");
  } catch (err) {
    console.error("NO ERROR:", err);
    alert("NO stored procedure failed");
  }
};
/* ================== BACK STEP ================== */
const backStep = async () => {
  if (step <= 1) return;

  const newStep = step - 1;

  setStep(newStep); // change step first

  try {
    await autoSave(form, newStep); // save correct step
  } catch (err) {
    console.error("Back autosave error:", err);
  }
};

const onOtpVerified = () => {
  otpRequestRef.current = false;
  setOtpVerified(true);
  localStorage.removeItem("justSentOtp");
  setError("");

  if (panExists) {
    // Existing PAN → show Name/Email with Next/Back buttons
    setStep(2);
  } else {
    // New PAN → go directly to Date of Birth step
    setStep(4);
  }
};

  /* ================== RENDER STEP ================== */
const renderStep = () => {
  switch (step) {
    case 1:
      return (
        <>
          {/* PAN Input Form */}
          <div id="loginForm">
  <input type="text" name="fakeuser" style={{ display: 'none' }} />
<input type="password" name="fakepass" style={{ display: 'none' }} />


 <input
  type="text"
  name="pan_random"
  autoComplete="new-password"
  placeholder="PAN (ABCDE1234F)"
  autoCorrect="off"
  autoCapitalize="off"
  spellCheck="false"
  value={form.pan}
  ref={panRef}
  maxLength={10}
  onChange={(e) => {
    const value = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 10);

    setForm(prev => ({ ...prev, pan: value }));
    setOtpVerified(false);
    setPanExists(false);
  }}
/>
            {/* Verify PAN button */}
            <button
              type="button"
              disabled={form.pan.length !== 10} // only enable if PAN is complete
              onClick={() => checkPan(form.pan)}
            >
              Verify PAN
            </button>
          </div>
        </>
      );
      case 2:
  return (
    <>
      {/* Name, Email, Mobile inputs */}
      {fieldErrors.full_name && <small className="field-error">{fieldErrors.full_name}</small>}
      <input
        name="full_name"
        placeholder="Full Name"
        value={form.full_name || ""}
        onChange={update}
      />

      {fieldErrors.email && <small className="field-error">{fieldErrors.email}</small>}
      <input
        name="email"
        placeholder="Email"
        value={form.email || ""}
        onChange={update}
      />

      <div style={{ display: "flex", gap: "8px" }}>
        <select
          name="country_code"
          value={form.country_code || "+91"}
          onChange={update}
        >
          {countryCodes.map(code => <option key={code} value={code}>{code}</option>)}
        </select>

        {fieldErrors.mobile && <small className="field-error">{fieldErrors.mobile}</small>}
        <input
          name="mobile"
          placeholder="Mobile (without country code)"
          value={form.mobile || ""}
          onChange={update}
        />
      </div>

      {/* 🔥 CONDITIONAL BUTTONS 🔥 */}
      {panExists ? (
        // Existing PAN → show Next / Back buttons only
        <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
          <button onClick={backStep}>Back</button>
          <button onClick={nextStep} disabled={!form.full_name || !form.email || !form.mobile}>
            Next
          </button>
        </div>
      ) : (
        // New PAN → show Send OTP button
        <button
         type="button"
          disabled={sendingOtp || step !== 2}
          onClick={async () => {

  // 🔒 HARD LOCK – prevents duplicate OTP calls
  if (otpRequestRef.current) return;
  otpRequestRef.current = true;

  if (sendingOtp) return;
  if (!validateStep()) {
    otpRequestRef.current = false;
    return;
  }

  setSendingOtp(true);

  try {
    await autoSave(form, 2);

    await axios.post("/api/send-otp", {
      pan: form.pan,
      mobile: `${form.country_code}${form.mobile}`
    });

    setOtpVerified(false);
    setStep(3);

    setSuccess("OTP sent successfully");
    setError("");

  } catch (err) {
    setError(err.response?.data?.message || "Failed to send OTP");
    otpRequestRef.current = false;   // unlock if failed
  } finally {
    setSendingOtp(false);
  }
}}
        >
          {sendingOtp ? "Sending OTP..." : "Send OTP"}
        </button>
      )}
    </>
  );


    case 3:
  return !otpVerified ? (
    <OnboardingOTP
      pan={form.pan}
      maskedMobile={`${form.country_code} ${form.mobile}`}
      onVerified={onOtpVerified} // ✅ this callback runs after OTP is verified
    />
  ) : (
    <>
      <p>OTP already verified ✅</p>
      <button onClick={() => setStep(4)}>Continue</button>
    </>
  );
   case 4:
  return (
    <>
      {/* Date of Birth */}
<div className="field-group">
  <label>Date of Birth</label>
  {fieldErrors.date_of_birth && (
    <small className="field-error">{fieldErrors.date_of_birth}</small>
  )}
  <div className="dob-wrapper" style={{ position: "relative" }}>
    {/* Hidden calendar input */}
    <input
      type="date"
      style={{ position: "absolute", opacity: 0, zIndex: -1 }}
      value={form.date_of_birth || ""}
      onChange={(e) => {
  const val = e.target.value;

  setForm(prev => {
    const updated = {
      ...prev,
      date_of_birth: val
    };

    // 🔥 autosave for admin + user
    debouncedAutoSave(updated, 4); // DOB is always step 4

    return updated;
  });

  setDobText(formatDateToText(val));
}}
        
    />

    {/* Manual typing input */}
<input
  type="text"
  placeholder="dd/MMM/yyyy"
  value={dobText}
  onChange={(e) => {
    const text = e.target.value;
    setDobText(text); // always update visible text
    const parsed = parseTextToDate(text); // parse to YYYY-MM-DD
      setForm(prev => {
  const updated = {
    ...prev,
    date_of_birth: parsed || ""
  };

  // 🔥 autosave for user + admin
  debouncedAutoSave(updated, 4); // always step 4 for DOB

  return updated;
});

  }}
/>

    {/* Calendar button */}
    <button
      type="button"
      onClick={(e) => {
        const dateInput = e.currentTarget.previousSibling.previousSibling;
        dateInput.showPicker?.();
      }}
      style={{ border: "none", background: "none", cursor: "pointer" }}
    >
      📅
    </button>
  </div>
</div>
      {/* Gender */}
      {fieldErrors.gender && (
        <small className="field-error">{fieldErrors.gender}</small>
      )}
      <select
        name="gender"
        value={form.gender || ""}
        onChange={update}
      >
        <option value="">Gender</option>
        <option>Male</option>
        <option>Female</option>
      </select>

      {/* Occupation */}
      {fieldErrors.occupation && (
        <small className="field-error">{fieldErrors.occupation}</small>
      )}
      <select
        name="occupation"
        value={form.occupation || ""}
        onChange={update}
      >
        <option value="">Occupation</option>
        {occupations.map(o => <option key={o}>{o}</option>)}
      </select>

      {/* Marital Status */}
      {fieldErrors.marital_status && (
        <small className="field-error">{fieldErrors.marital_status}</small>
      )}
      <select
        name="marital_status"
        value={form.marital_status || ""}
        onChange={update}
      >
        <option value="">Marital Status</option>
        {maritalStatuses.map(m => <option key={m}>{m}</option>)}
      </select>

      <div className="step-buttons">
  <button type="button" onClick={backStep}>Back</button>
<button type="button" onClick={nextStep}>Next</button>
</div>
    </>
  );

    case 5: 
  return (
    <>
      {fieldErrors.address_line && <small className="field-error">{fieldErrors.address_line}</small>}
      <input
        name="address_line"
        placeholder="Address"
        title="Enter house / flat number and building name"
        value={form.address_line || ""}
        onChange={update}
      />

      {fieldErrors.street && <small className="field-error">{fieldErrors.street}</small>}
      <input
        name="street"
        placeholder="Street"
        title="Enter street or road name"
        value={form.street || ""}
        onChange={update}
      />

      {fieldErrors.area && <small className="field-error">{fieldErrors.area}</small>}
      <input
        name="area"
        placeholder="Area"
        title="Enter area / locality"
        value={form.area || ""}
        onChange={update}
      />

      {fieldErrors.city && <small className="field-error">{fieldErrors.city}</small>}
      <input
        name="city"
        placeholder="City"
        title="Enter city name"
        value={form.city || ""}
        onChange={update}
      />

      {fieldErrors.state && <small className="field-error">{fieldErrors.state}</small>}
      <select
        name="state"
        title="Select your state"
        value={form.state || ""}
        onChange={update}
      >
        <option value="">Select State</option>
        {indianStates.map(s => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>

      {fieldErrors.pincode && <small className="field-error">{fieldErrors.pincode}</small>}
      <input
        name="pincode"
        placeholder="Pincode"
        title="Enter 6-digit pincode"
        value={form.pincode || ""}
        onChange={update}
        maxLength={6}
      />

      <div className="step-buttons">
  <button onClick={backStep}>Back</button>
  <button onClick={nextStep}>Next</button>
</div>
    </>
  );
     case 6:
  return (
    <>
      {fieldErrors.bank_name && <small className="field-error">{fieldErrors.bank_name}</small>}
      <input
        name="bank_name"
        placeholder="Bank Name"
        title="Enter your bank name"
        value={form.bank_name || ""}
        onChange={update}
      />

      {fieldErrors.branch_name && <small className="field-error">{fieldErrors.branch_name}</small>}
      <input
        name="branch_name"
        placeholder="Branch Name"
        title="Enter your bank branch name"
        value={form.branch_name || ""}
        onChange={update}
      />

      {fieldErrors.account_type && <small className="field-error">{fieldErrors.account_type}</small>}
      <select
        name="account_type"
        title="Select bank account type (Savings or Current)"
        value={form.account_type || ""}
        onChange={update}
      >
        <option value="">Select Account Type</option>
        {accountTypes.map(a => (
          <option key={a} value={a}>{a}</option>
        ))}
      </select>

      {fieldErrors.account_number && <small className="field-error">{fieldErrors.account_number}</small>}
      <input
        name="account_number"
        placeholder="Account Number"
        title="Enter your bank account number"
        value={form.account_number || ""}
        onChange={update}
        maxLength={18}
      />

      {fieldErrors.ifsc && <small className="field-error">{fieldErrors.ifsc}</small>}
      <input
        name="ifsc"
        placeholder="IFSC"
        title="Enter 11-character IFSC code"
        value={form.ifsc || ""}
        onChange={update}
        maxLength={11}
      />

      {fieldErrors.micr && <small className="field-error">{fieldErrors.micr}</small>}
      <input
        name="micr"
        placeholder="MICR"
        title="Enter 9-digit MICR code (optional)"
        value={form.micr || ""}
        onChange={update}
        maxLength={9}
      />

     <div className="step-buttons">
  <button onClick={backStep}>Back</button>
  <button onClick={nextStep}>Next</button>
</div>

    </>
  );

case 7:
  return (
    <>
      {/* Nominee Basic Info */}
      {fieldErrors.nominee_name && <small className="field-error">{fieldErrors.nominee_name}</small>}
      <input
        name="nominee_name"
        placeholder="Nominee Name"
        title="Enter nominee full name"
        value={form.nominee_name || ""}
        onChange={update}
      />

      {fieldErrors.nominee_relation && <small className="field-error">{fieldErrors.nominee_relation}</small>}
      <select
        name="nominee_relation"
        title="Select nominee relation"
        value={form.nominee_relation || ""}
        onChange={update}
      >
        <option value="">Select Relation</option>
        {nomineeRelations.map(r => <option key={r} value={r}>{r}</option>)}
      </select>

      {fieldErrors.nominee_id_type && <small className="field-error">{fieldErrors.nominee_id_type}</small>}
      <select
        name="nominee_id_type"
        title="Select nominee ID type"
        value={form.nominee_id_type || ""}
        onChange={update}
      >
        <option value="">Select ID Type</option>
        {nomineeIdTypes.map(id => <option key={id} value={id}>{id}</option>)}
      </select>

      {fieldErrors.nominee_id_number && <small className="field-error">{fieldErrors.nominee_id_number}</small>}
      <input
        name="nominee_id_number"
        data-type={form.nominee_id_type} 
        placeholder="Nominee ID Number"
        title="Enter nominee ID number"
        value={form.nominee_id_number || ""}
        onChange={update}
      />

      {fieldErrors.nominee_email && <small className="field-error">{fieldErrors.nominee_email}</small>}
      <input
        name="nominee_email"
        placeholder="Nominee Email"
        title="Enter nominee email address"
        value={form.nominee_email || ""}
        onChange={update}
      />

      {fieldErrors.nominee_contact && <small className="field-error">{fieldErrors.nominee_contact}</small>}
      <input
        name="nominee_contact"
        placeholder="Nominee Contact"
        title="Enter nominee mobile number"
        value={form.nominee_contact || ""}
        onChange={update}
        maxLength={10}
      />

      <select
        name="nominee_is_minor"
        title="Select whether nominee is minor or adult"
        value={form.nominee_is_minor || "No"}
        onChange={update}
      >
        <option value="No">Adult</option>
        <option value="Yes">Minor</option>
      </select>

     {/* Minor Details */}
{form.nominee_is_minor === "Yes" && (
  <>
    {fieldErrors.nominee_dob && (
      <small className="field-error">{fieldErrors.nominee_dob}</small>
    )}
    <div className="dob-wrapper" style={{ position: "relative" }}>
      {/* Hidden date input */}
      <input
        type="date"
        style={{ position: "absolute", opacity: 0, zIndex: -1 }}
        value={form.nominee_dob || ""}
        onChange={(e) => {
  const val = e.target.value;

  setForm(prev => {
    const updated = {
      ...prev,
      nominee_dob: val
    };

    debouncedAutoSave(updated, adminMode ? adminStep : step);

    return updated;
  });

  setNomineeDobText(formatDateToText(val));
}}
      />
      {/* Visible text input */}
      <input
        type="text"
        placeholder="dd/MMM/yyyy"
        value={nomineeDobText}
        onChange={(e) => {
          const text = e.target.value;
          setNomineeDobText(text);
          const parsed = parseTextToDate(text);
          setForm(prev => {
  const updated = { ...prev, nominee_dob: parsed || "" };

  // autosave
  debouncedAutoSave(updated, step);

  return updated;
});
        }}
      />
      {/* Calendar picker button */}
      <button
        type="button"
        onClick={(e) => {
          const dateInput = e.currentTarget.previousSibling.previousSibling;
          dateInput.showPicker?.();
        }}
      >
        📅
      </button>
    </div>

    {/* Guardian Name */}
    {fieldErrors.guardian_name && (
      <small className="field-error">{fieldErrors.guardian_name}</small>
    )}
    <input
      name="guardian_name"
      placeholder="Guardian Name"
      title="Enter guardian name (required for minor)"
      value={form.guardian_name || ""}
      onChange={update}
    />
  </>
)}

     {/* Nominee Address Header Row */}
<div className="nominee-header-row">
  <span className="nominee-heading">Nominee Address</span>
<label className="same-address-inline">
  <input
    type="checkbox"
    checked={form.same_address || false}
    onChange={(e) => {
      const checked = e.target.checked;
      setForm(prev => {
        const updated = {
          ...prev,
          same_address: checked,
          nominee_address: checked ? prev.address_line : "",
          nominee_street: checked ? prev.street : "",
          nominee_area: checked ? prev.area : "",
          nominee_city: checked ? prev.city : "",
          nominee_state: checked ? prev.state : "",
          nominee_pincode: checked ? prev.pincode : ""
        };

        return updated;
      });
    }}
  />
  <span className="same-address-text">Same as Client</span>
</label>

</div>
      {/* Nominee Address Fields */}
      {fieldErrors.nominee_address && <small className="field-error">{fieldErrors.nominee_address}</small>}
      <input
        name="nominee_address"
        placeholder="Nominee Address"
        title="Enter nominee address"
        value={form.nominee_address || ""}
        onChange={update}
        disabled={form.same_address}
      />
      {fieldErrors.nominee_street && <small className="field-error">{fieldErrors.nominee_street}</small>}
      <input
        name="nominee_street"
        placeholder="Nominee Street"
        title="Enter nominee street"
        value={form.nominee_street || ""}
        onChange={update}
        disabled={form.same_address}
      />
      {fieldErrors.nominee_area && <small className="field-error">{fieldErrors.nominee_area}</small>}
      <input
        name="nominee_area"
        placeholder="Nominee Area"
        title="Enter nominee area"
        value={form.nominee_area || ""}
        onChange={update}
        disabled={form.same_address}
      />
      {fieldErrors.nominee_city && <small className="field-error">{fieldErrors.nominee_city}</small>}
      <input
        name="nominee_city"
        placeholder="Nominee City"
        title="Enter nominee city"
        value={form.nominee_city || ""}
        onChange={update}
        disabled={form.same_address}
      />
      {fieldErrors.nominee_state && <small className="field-error">{fieldErrors.nominee_state}</small>}
      <select
        name="nominee_state"
        title="Select nominee state"
        value={form.nominee_state || ""}
        onChange={update}
        disabled={form.same_address}
      >
        <option value="">Select State</option>
        {indianStates.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      {fieldErrors.nominee_pincode && <small className="field-error">{fieldErrors.nominee_pincode}</small>}
      <input
        name="nominee_pincode"
        placeholder="Nominee Pincode"
        title="Enter 6-digit nominee pincode"
        value={form.nominee_pincode || ""}
        onChange={update}
        maxLength={6}
        disabled={form.same_address}
      />

      <div className="step-buttons">
  <button onClick={backStep}>Back</button>
  <button onClick={handleSubmit}>Submit</button>
</div>
    </>
  );
  case STEP_CONFIRM:
  return (
    <OnlineTransactionConfirm
      onYes={handleYes}
      onNo={handleNo}
    />
  );
  case STEP_PDF:
  return (
    <>
      <h3>Download Documents</h3>

      <button
  onClick={async () => {
    try {
      const res = await axios.get(
  `/api/pdf/generate-pdf?pan=${form.pan}`,
  { responseType: "blob" }
);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      window.open(url, "_blank"); // open filled PDF
    } catch (err) {
      console.error("AOF PDF error:", err);
      alert("AOF PDF not available. Please try again.");
    }
  }}
>
  Download AOF PDF
</button>
      <button
  onClick={async () => {
    try {
      const res = await axios.get(
        `/api/pdf/download-sip-mandate?pan=${form.pan}`,
        { responseType: "blob" }
      );


      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "SIP_Mandate.pdf";
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("SIP Mandate error:", err);
      alert("SIP Mandate not available. Please try again.");
    }
  }}
>
  Download SIP Mandate
</button>

      {/* 👇 NEW BUTTON */}
      <button
        style={{ marginTop: "20px" }}
        onClick={() => setStep(STEP_DOCUMENT_UPLOAD)}
      >
        Continue to Document Upload
      </button>
    </>
  );
  case STEP_DOCUMENT_UPLOAD:
  return (
    <ClientDocumentUpload
      pan={form.pan}
      onRiskProfile={() => setStep(STEP_RISK_PROFILE)}
    />
  );

case STEP_RISK_PROFILE:
  return (
    <RiskProfile
      clientName={form.full_name}
      clientCode={`WS${form.pan}`}
      filledDate={new Date().toLocaleDateString("en-GB")}
    />
  );
      default: return null;
    }
  };


  return (
  <div className="page">
    {/* HEADER */}
    <header className="header">
      <div className="logo-area">
        <img
  src="/wfmf/images/logo.png"
  alt="WealthFirst Logo"
  className="logo"
  loading="lazy"
/>

        <span className="logo-text">WealthFirst</span>
      </div>
      {/* Admin button removed from client-facing page.
          Admin dashboard is now reached only from the internal
          .aspx portal (Information.aspx) via a protected button there. */}
    </header>

    {/* MAIN SECTION */}
    <div className={`main ${step >= 4 ? "center-form" : ""}`}>

      {/* LEFT IMAGE (STEP 1–3) */}
      {(step >= 1 && step <= 3) && (
        <div className={`image-area step-${step}`}>
          <img
  src={
    step === 1
      ? "/wfmf/images/pan.png"
      : step === 2
      ? "/wfmf/images/personal.png"
      : "/wfmf/images/otp.webp"
      
  }
  alt="Onboarding"
  loading="lazy"
/>

        </div>
      )}

      {/* FORM AREA */}
      <div className="form-area">
        <form
  className="form-card"
  onSubmit={(e) => e.preventDefault()}
>

          <h2>
            <span>eKYC</span> Online Registration
          </h2>
          <p className="subtitle">
            Open a trading and Demat account online.
          </p>

          {/* 🔴 ERROR MESSAGE BOX */}
          {error && (
            <div className="error-box">
              {error}
            </div>
          )}

          {/* 🔹 STEP CONTENT */}
          {renderStep()}
        </form>

      </div>

    </div>

    {/* ✅ CLIENT SUMMARY MODAL */}
    {showClientSummary && (
      <div className="client-summary-backdrop">
        <div className="client-summary">
          <h3>Client Details</h3>
          <div className="field">
            <label>PAN:</label>
            <span>{form.pan}</span>
          </div>
          <div className="field">
            <label>Email:</label>
            <span>{form.email}</span>
          </div>
          <div className="field">
            <label>Mobile:</label>
            <span>{form.country_code} {form.mobile}</span>
          </div>
        </div>
      </div>
    )}

  </div>
);
}